import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle, Briefcase, Image } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  color: string;
  delay: number;
}

const ActionButton: React.FC<ActionButtonProps> = ({ icon, label, onClick, color, delay }) => (
  <motion.button
    initial={{ opacity: 0, y: 20, scale: 0.8 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 20, scale: 0.8 }}
    transition={{ delay: delay * 0.1 }}
    onClick={onClick}
    className={`flex items-center justify-center p-3 rounded-full text-white shadow-lg ${color} hover:opacity-90 transition-opacity`}
    aria-label={label}
  >
    {icon}
  </motion.button>
);

export const QuickActionButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [hideForModal, setHideForModal] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Hide when Services details modal is open (body has class 'modal-open-services')
  useEffect(() => {
    const update = () => setHideForModal(document.body.classList.contains('modal-open-services'));
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const actions = [
    {
      icon: <MessageCircle size={20} />,
      label: 'Messages',
      onClick: () => navigate('/messages'),
      color: 'bg-blue-600',
    },
    {
      icon: <Briefcase size={20} />,
      label: 'Post Requirement',
      onClick: () => navigate('/requirements/post'),
      color: 'bg-green-500',
    },
    {
      icon: <Image size={20} />,
      label: 'New Post',
      onClick: () => navigate('/explore?newPost=true'),
      color: 'bg-purple-500',
    },
  ];

  if (hideForModal) return null;

  return (
    <div
      className="xl:hidden fixed bottom-20 right-4 sm:right-6 z-40 flex flex-col items-center"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      ref={menuRef}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="absolute bottom-20 right-0 flex flex-col items-center space-y-3 mb-2"
          >
            {actions.map((action, index) => (
              <ActionButton
                key={action.label}
                {...action}
                delay={actions.length - index}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-col items-center">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => navigate('/create')}
          className={`p-4 rounded-full shadow-md shadow-purple-950/60 bg-gradient-to-r from-[#6B21A8] to-[#581C87] text-white border border-purple-500/40 focus:outline-none focus:ring-2 focus:ring-purple-400`}
          aria-label={'Create'}
          title="Create"
        >
          <Plus size={24} />
        </motion.button>
        <span className="mt-2 text-xs font-semibold text-slate-300">Create</span>
      </div>
    </div>
  );
};
